import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { EngagementBudgetService } from "@services/engagements/budget/engagementBudgetService"
import type {
  EngagementBudgetHeader,
  EngagementBudgetTree,
} from "@models/engagementBudgetTree.model"
import { EngagementLaunchPolicyPayload } from "@services/engagements/budget/commands/cmdUpsertEngagementLaunchPolicy"
import { LaunchNetworkPolicyPayload } from "@services/engagements/budget/commands/cmdUpsertLaunchNetworkPolicy"
import { AudienceNetworkPolicyPayload } from "@services/engagements/budget/commands/cmdUpsertAudienceNetworkPolicy"
import { LaunchBudgetSavePayload } from "@services/engagements/budget/commands/cmdSaveLaunchBudget"

export function useEngagementBudget(engagementId: number) {
  const service = useMemo(() => new EngagementBudgetService(), [])
  const [header, setHeader] = useState<EngagementBudgetHeader | null>(null)
  const [tree, setTree] = useState<EngagementBudgetTree | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const version = useRef(0)

  const refetch = useCallback(async () => {
    const v = ++version.current
    try {
      const [h, t] = await Promise.all([
        service.getHeader(engagementId),
        service.getTree(engagementId),
      ])
      if (v !== version.current) return
      setHeader(h)
      setTree(t)
      setError(null)
    } catch (err: any) {
      if (v !== version.current) return
      setError(err?.message || "Failed to load budget")
    }
  }, [engagementId, service])

  useEffect(() => {
    let active = true
    setLoading(true)
    refetch().finally(() => {
      if (active) setLoading(false)
    })
    return () => {
      active = false
    }
  }, [refetch])

  /** Commit a campaign's whole budget draft, then refetch so the screen reads the
   *  server's resolution rather than its own arithmetic (MPN-1802). */
  const saveLaunchBudget = useCallback(
    async (launchId: number, payload: LaunchBudgetSavePayload) => {
      await service.saveLaunchBudget(launchId, payload)
      await refetch()
    },
    [refetch, service],
  )

  const migrateLaunchBudget = useCallback(
    async (launchId: number) => {
      await service.migrateLaunchBudget(launchId)
      await refetch()
    },
    [refetch, service],
  )

  const setLaunchPolicy = useCallback(
    async (payload: EngagementLaunchPolicyPayload) => {
      await service.setLaunchPolicy(engagementId, payload)
      await refetch()
    },
    [engagementId, refetch, service],
  )

  const clearLaunchPolicy = useCallback(
    async (launchId: number) => {
      await service.clearLaunchPolicy(engagementId, launchId)
      await refetch()
    },
    [engagementId, refetch, service],
  )

  const setNetworkPolicy = useCallback(
    async (launchId: number, payload: LaunchNetworkPolicyPayload) => {
      await service.setNetworkPolicy(launchId, payload)
      await refetch()
    },
    [refetch, service],
  )

  const clearNetworkPolicy = useCallback(
    async (launchId: number, network: string) => {
      await service.clearNetworkPolicy(launchId, network)
      await refetch()
    },
    [refetch, service],
  )

  // A network's daily spend cap cascades to the audiences under it: set the
  // network policy, then upsert each audience's policy with the same cap, then
  // refetch once.
  const setNetworkPolicyWithAudiences = useCallback(
    async (
      launchId: number,
      networkPayload: LaunchNetworkPolicyPayload,
      adGroupPayloads: { adGroupId: number; payload: AudienceNetworkPolicyPayload }[],
    ) => {
      await service.setNetworkPolicy(launchId, networkPayload)
      await Promise.all(
        adGroupPayloads.map(({ adGroupId, payload }) =>
          service.setAudiencePolicy(launchId, adGroupId, payload),
        ),
      )
      await refetch()
    },
    [refetch, service],
  )

  const setAudiencePolicy = useCallback(
    async (launchId: number, adGroupId: number, payload: AudienceNetworkPolicyPayload) => {
      await service.setAudiencePolicy(launchId, adGroupId, payload)
      await refetch()
    },
    [refetch, service],
  )

  const clearAudiencePolicy = useCallback(
    async (launchId: number, adGroupId: number, network: string) => {
      await service.clearAudiencePolicy(launchId, adGroupId, network)
      await refetch()
    },
    [refetch, service],
  )

  return {
    header,
    tree,
    loading,
    error,
    refetch,
    saveLaunchBudget,
    migrateLaunchBudget,
    setLaunchPolicy,
    clearLaunchPolicy,
    setNetworkPolicy,
    clearNetworkPolicy,
    setNetworkPolicyWithAudiences,
    setAudiencePolicy,
    clearAudiencePolicy,
  }
}
